import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import field from '@sorare/core/src/assets/icons/field.svg';
import { LineupBonuses } from '@sorare/core/src/atoms/icons/LineupBonuses';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, BrandS, BrandXL } from '@sorare/core/src/atoms/typography';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { range } from '@sorare/core/src/lib/arrays';

import type { LineupBonusesExplanation_so5AppearanceBonusInterface } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ center: true })`
  border-radius: var(--intermediate-unit);
  border: 2px solid var(--c-onBase-success);
  padding: var(--double-unit);
  box-shadow: 0 0 20px rgba(var(--c-rgb-onBase-success), 0.3) inset;
  background:
    linear-gradient(to top, var(--c-black), var(--c-nl-700)),
    linear-gradient(
      rgba(var(--c-rgb-onBase-success), 0.3),
      rgba(var(--c-rgb-onBase-success), 0.1)
    ),
    linear-gradient(to top, var(--c-nl-800), var(--c-nl-900), transparent),
    no-repeat center / cover url(${field}),
    linear-gradient(
      to bottom,
      rgba(var(--c-rgb-black), 0.8),
      rgba(var(--c-rgb-black), 0.9),
      rgba(var(--c-rgb-black), 0.8)
    );
`;
const Title = styled(BrandXL)`
  text-shadow: 0px 0px 10px var(--c-white);
`;
const ProgressBarWrapper = styled.div`
  width: 50%;
`;

type Props = {
  lineupBonuses: LineupBonusesExplanation_so5AppearanceBonusInterface[];
};
export const LineupBonusesExplanation = ({ lineupBonuses }: Props) => {
  const max =
    lineupBonuses.reduce(
      (sum, { maxBonusBasisPoints }) => sum + maxBonusBasisPoints,
      0
    ) / 100;
  const activeBonus =
    lineupBonuses.reduce(
      (sum, { bonusBasisPoints }) => sum + bonusBasisPoints,
      0
    ) / 10000;

  return (
    <Root>
      <Horizontal>
        <Title>
          <FormattedNumber
            value={activeBonus}
            style="percent"
            signDisplay="always"
          />
        </Title>
        <LineupBonuses size={5} inactive={!activeBonus} />
      </Horizontal>
      <BrandS>
        <FormattedMessage defaultMessage="Lineup Bonus" />
      </BrandS>
      <Vertical gap={1.5} center>
        <ProgressBarWrapper>
          <SplitProgressBar
            color="var(--c-white)"
            currentScore={activeBonus}
            thresholds={range(max).map(i => ({ score: (i + 1) * 0.01 }))}
          />
        </ProgressBarWrapper>
        <BodyS className="text-center">
          <FormattedMessage defaultMessage="Earn additional team bonuses for building specific lineups with constraints." />
        </BodyS>
      </Vertical>
    </Root>
  );
};

LineupBonusesExplanation.fragments = {
  so5AppearanceBonusInterface: gql`
    fragment LineupBonusesExplanation_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      bonusBasisPoints
      maxBonusBasisPoints
    }
  ` as TypedDocumentNode<LineupBonusesExplanation_so5AppearanceBonusInterface>,
};
